import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { EditButton, NewPhaseForm } from "../../components";
import { useProject } from "../../hooks";
import { parseAmount } from "../../services/utils";
import PhasePlotGrid from "./PhasePlotsGrid";

function ProjectPhase({ data, isNew = false, projectData }) {
  const [phaseData, setPhaseData] = useState({});
  const [plots, setPlots] = useState({});
  useEffect(() => {
    setPhaseData({
      ...data,
      emi: data.emi ? `${data.emi}`?.replace(/.00|,/g, "") : "",
      start_range: data.start_range && `${data.start_range}`.replace(".00", "").replace(/,/g, ""),
      end_range: data.end_range && `${data.end_range}`.replace(".00", "").replace(/,/g, ""),
    });
    setPlots({
      ongoing_plots: data.ongoing_plots,
      completed_plots: data.completed_plots,
      available_plots: data.available_plots,
      canceled_plots: data.canceled_plots,
      on_hold_plots: data.on_hold_plots,
      unavailable_plots: data.unavailable_plots,
    });
  }, [data]);

  useEffect(() => {
    setPhaseStatus(phaseData.status);
  }, [phaseData]);
  const [phaseStatus, setPhaseStatus] = useState(phaseData.status);
  const { updatePhaseById } = useProject();
  const updatePhase = async (editedData) => {
    const { success, error } = await updatePhaseById(phaseData.project_id, phaseData.id, {
      ...editedData,
      status: phaseStatus,
    });
    if (error) return toast.error(error);
    toast.success(success);
    window.location.reload();
    setShowPhaseEdit(false);
  };

  const [showPhaseEdit, setShowPhaseEdit] = useState(false);

  return (
    <div className="column">
      {!isNew && (
        <section className="row">
          <span>{phaseData.phase_name ?? `Phase ${phaseData.phase_number}`} details</span>
          <div className="row end">
            <span className="label">Close to sale</span>
            <div className="toggle">
              <div className="toggle_switch">
                <input
                  type="checkbox"
                  className="checkbox"
                  name={`sale_toggle_${phaseData.id}`}
                  id={`sale_toggle_${phaseData.id}`}
                  checked={phaseStatus === "open"}
                  onChange={() => setPhaseStatus(phaseStatus === "open" ? "closed" : "open")}
                  disabled
                />
                <label htmlFor={`sale_toggle_${phaseData.id}`} className="label">
                  <span className="inner"></span>
                  <span className="switch"></span>
                </label>
              </div>
            </div>
            <span className="label">Open to sale</span>
            <EditButton className="ghost_gray_no_underline" small onClick={() => setShowPhaseEdit(true)} />
          </div>
        </section>
      )}

      {showPhaseEdit ? (
        <NewPhaseForm
          data={phaseData}
          isEdit={true}
          handleSubmit={updatePhase}
          handleCancel={() => setShowPhaseEdit(false)}
          projectData={{ ...projectData, phase_name: `Phase ${phaseData.phase_number}` }}
        />
      ) : (
        <section className="section grid_3">
          <div className="column start">
            <div className="row">
              <span className="label">Agent commission</span>
              <span className="value">{`${phaseData.agent_commission}`.replace(".00", "")}%</span>
            </div>
            <div className="row">
              <span className="label">Phase name</span>
              <span className="value">{phaseData.phase_name ?? `Phase ${phaseData.phase_number}`}</span>
            </div>
            <div className="row">
              <span className="label">Number of plots</span>
              <span className="value">{phaseData.number_of_plots}</span>
            </div>
            <div className="row">
              <span className="label">Company's Commission</span>
              <span className="value">{`${phaseData.company_commission}`.replace(".00", "")}%</span>
            </div>
            <div className="row rate_range">
              <div className="input_with_prefix_wrapper">
                <span className="prefix">₹</span>
                <input
                  type="number"
                  className="input"
                  defaultValue={phaseData.start_range}
                  placeholder="Start Range"
                  min={0}
                  max={phaseData.end_range}
                  readOnly
                />
              </div>
              <span className="label">Rate Range</span>
              <div className="input_with_prefix_wrapper">
                <span className="prefix">₹</span>
                <input
                  type="number"
                  className="input"
                  defaultValue={phaseData.end_range}
                  placeholder="End Range"
                  min={phaseData.start_range}
                  readOnly
                />
              </div>
            </div>
            <div className="row">
              <span className="label">EMI Rate Limit (per sq.ft.)</span>
              <span className="value">{parseAmount(phaseData.max_negotiate_rate)}</span>
            </div>
            <div className="row">
              <span className="label">EMI</span>
              <span className="value">
                {phaseData.emi} {phaseData.emi < 2 ? "month" : "months"}
              </span>
            </div>
            <div className="row">
              <span className="label">Down Payment Rate Limit (per sq.ft.)</span>
              <span className="value">{parseAmount(phaseData.rate_limit)}</span>
            </div>
            <div className="row">
              <span className="label">Token percent</span>
              <span className="value">{`${phaseData.token_percent}`.replace(".00", "")}%</span>
            </div>
            <div className="row">
              <span className="label">Token percent limit</span>
              <span className="value">{`${phaseData.token_percent_limit}`.replace(".00", "")}%</span>
            </div>
            <div className="row">
              <span className="label">
                Registration Charges <br /> + Advocate Fees + Satbara
              </span>
              <span className="value">{parseAmount(phaseData.registration_charges)}</span>
            </div>
            <div className="row">
              <span className="label">
                Development Charges <br />
                (Water & Electricity)
              </span>
              <span className="value">{parseAmount(phaseData.development_charges)}</span>
            </div>
            <div className="row">
              <span className="label">Put on hold</span>
              <span className="value">{phaseData.days_to_put_on_hold} days</span>
            </div>
            <div className="row">
              <span className="label">Rate/day to hold plot</span>
              <span className="value">{parseAmount(phaseData.rate_per_day_to_hold_plot)}</span>
            </div>
          </div>
          <div className="vertical_divider"></div>
          <div className="column">
            <div className="row">
              <span>{phaseData.phase_name}</span>
              <span className="small">(Total Plots - {phaseData.number_of_plots})</span>
            </div>
            <PhasePlotGrid
              projectId={phaseData.project_id}
              phaseId={phaseData.phase_number}
              plotCount={phaseData.number_of_plots}
              data={plots}
              isNew={isNew}
            />
          </div>
        </section>
      )}
    </div>
  );
}

export default ProjectPhase;
